import { useState, FormEvent } from 'react';
import BookingCalendar from './components/BookingCalendar';
import { Link } from 'react-router-dom';

interface FaqItem {
  question: string;
  answer: string | string[];
}

const faqData: FaqItem[] = [
  {
    question: 'Can I complete everything online?',
    answer: 'Yes.\nFrom inquiry and screening to contract and payment, everything can be completed online.',
  },
  {
    question: 'Do I need a Japanese guarantor?',
    answer: 'No.\nWe do not require a guarantor or guarantee company.',
  },
  {
    question: 'What documents are required?',
    answer: [
      'Passport',
      'Residence Card (在留カード)',
      'Contact information',
      'Emergency contact',
    ],
  },
  {
    question: 'How long does the screening take?',
    answer: 'Usually 1–3 business days after document submission.',
  },
  {
    question: 'What is the minimum stay?',
    answer: 'The minimum stay is 1 month.\nFlexible stays such as 45 days or 2.5 months are also possible.',
  },
  {
    question: 'Can I register this address?',
    answer: 'Yes.\nYou can use this address for residence registration, bank accounts, job search, and business preparation.',
  },
  {
    question: 'Is this a hotel or an apartment?',
    answer: 'Neither.\nThis is a coliving‑style rental residence designed as a bridge to long‑term life in Japan.',
  },
  {
    question: 'What payment methods are available?',
    answer: [
      'Credit card',
      'International transfer (Wise, etc.)',
      'Japanese bank transfer (if available)',
    ],
  },
  {
    question: 'Is in‑person check‑in required?',
    answer: 'No.\nWe use smart locks, so remote or late check‑in is possible.',
  },
  {
    question: 'What support is available after move‑in?',
    answer: 'We support daily life setup, next housing steps, work or startup preparation, and long‑term relocation in Takayama.',
  },
];

function FaqAccordionItem({
  item,
  isOpen,
  onToggle,
}: {
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        className="w-full flex items-center justify-between py-6 px-2 text-left cursor-pointer group"
        onClick={onToggle}
      >
        <span className="text-lg font-medium text-gray-900 pr-8 group-hover:text-orange-500 transition-colors">
          {item.question}
        </span>
        <div
          className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
            isOpen ? 'bg-orange-400 rotate-180' : 'bg-gray-100'
          }`}
        >
          <i
            className={`ri-arrow-down-s-line text-lg ${
              isOpen ? 'text-white' : 'text-gray-500'
            }`}
          ></i>
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 pb-6' : 'max-h-0'
        }`}
      >
        <div className="px-2 text-gray-700 leading-relaxed">
          {Array.isArray(item.answer) ? (
            <ul className="space-y-2">
              {item.answer.map((line, i) => (
                <li key={i} className="flex items-start">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          ) : (
            item.answer.split('\n').map((line, i) => (
              <p key={i} className={i > 0 ? 'mt-2' : ''}>
                {line}
              </p>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBookingCalendar, setShowBookingCalendar] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleContactSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('name', contactForm.name);
      formData.append('email', contactForm.email);
      formData.append('subject', contactForm.subject);
      formData.append('message', contactForm.message);

      const response = await fetch('https://readdy.ai/api/form/d65lvuot4cagked7r420', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setContactForm({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle navbar scroll effect
  const handleScroll = () => {
    const isScrolled = window.scrollY > 50;
    setScrolled(isScrolled);
  };

  // Add scroll listener
  useState(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Booking Calendar Modal */}
      {showBookingCalendar && (
        <BookingCalendar onClose={() => setShowBookingCalendar(false)} />
      )}

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowContactForm(false)}
          ></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowContactForm(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>
            
            <div className="p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-4">
                  <i className="ri-mail-send-line text-3xl text-orange-500"></i>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Noto Serif JP' }}>
                  Questions or Requests?
                </h3>
                <p className="text-gray-600 text-sm">
                  Feel free to reach out with any questions, special requests, or inquiries.
                </p>
              </div>

              <form 
                id="contact-inquiry-form"
                data-readdy-form
                onSubmit={handleContactSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 text-sm"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 text-sm cursor-pointer"
                  >
                    <option value="">Select a subject</option>
                    <option value="Room Inquiry">Room Inquiry</option>
                    <option value="Availability Check">Availability Check</option>
                    <option value="Pricing Question">Pricing Question</option>
                    <option value="Special Request">Special Request</option>
                    <option value="General Question">General Question</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 text-sm resize-none"
                    placeholder="Please describe your question or request..."
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">
                    {contactForm.message.length}/500 characters
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center">
                    <div className="w-8 h-8 flex items-center justify-center bg-green-500 rounded-full mr-3">
                      <i className="ri-check-line text-white"></i>
                    </div>
                    <p className="text-green-700 text-sm">
                      Thank you for your message! We&apos;ll get back to you soon.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center">
                    <div className="w-8 h-8 flex items-center justify-center bg-red-500 rounded-full mr-3">
                      <i className="ri-close-line text-white"></i>
                    </div>
                    <p className="text-red-700 text-sm">
                      Something went wrong. Please try again later.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gray-900 text-white py-4 px-6 rounded-xl hover:bg-orange-400 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Send Message
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500">
                  For urgent inquiries, email us at{' '}
                  <a href="mailto:info@moshimoshi.info" className="text-orange-500 hover:underline cursor-pointer">
                    info@moshimoshi.info
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img 
                src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/f0377fb5ec1076e4cb9e676fd4f0df74.jpeg" 
                alt="Coliving HIDA TAKAYAMA"
                className="w-8 h-8 rounded-full object-cover"
              />
              <h1 className="text-xl font-semibold text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
                coliving HIDA TAKAYAMA
              </h1>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-6">
              <a href="#concept" className={`text-base transition-colors hover:text-orange-400 ${scrolled ? 'text-gray-600' : 'text-white'}`}>About</a>
              <a href="#rooms" className={`text-base transition-colors hover:text-orange-400 ${scrolled ? 'text-gray-600' : 'text-white'}`}>Rooms</a>
              <a href="#amenities" className={`text-base transition-colors hover:text-orange-400 ${scrolled ? 'text-gray-600' : 'text-white'}`}>Amenities</a>
              <a href="#location" className={`text-base transition-colors hover:text-orange-400 ${scrolled ? 'text-gray-600' : 'text-white'}`}>Discover</a>
              <a href="#booking" className={`text-base transition-colors hover:text-orange-400 ${scrolled ? 'text-gray-600' : 'text-white'}`}>How It Works</a>
              <a href="#faq" className={`text-base transition-colors hover:text-orange-400 ${scrolled ? 'text-gray-600' : 'text-white'}`}>FAQ</a>
              <a href="/availability" className={`text-base transition-colors hover:text-orange-400 ${scrolled ? 'text-gray-600' : 'text-white'}`}>Availability</a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={`text-2xl ${scrolled ? 'text-gray-900' : 'text-white'} ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-6 py-4 space-y-4">
              <a href="#concept" className="block text-lg text-gray-900 hover:text-orange-400 transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>About</a>
              <a href="#rooms" className="block text-lg text-gray-900 hover:text-orange-400 transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Rooms</a>
              <a href="#amenities" className="block text-lg text-gray-900 hover:text-orange-400 transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Amenities</a>
              <a href="#location" className="block text-lg text-gray-900 hover:text-orange-400 transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Discover</a>
              <a href="#booking" className="block text-lg text-gray-900 hover:text-orange-400 transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
              <a href="#faq" className="block text-lg text-gray-900 hover:text-orange-400 transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
              <a href="/availability" className="block text-lg text-gray-900 hover:text-orange-400 transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Availability</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-end justify-start overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/440895bb7a1b3dfc3d1522148fd49809.jpeg')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/40 mb-8">
              <span className="text-white text-sm font-medium tracking-wider uppercase">Digital Nomad Sanctuary</span>
            </div>
            
            <h2 className="text-6xl md:text-7xl font-light text-white leading-tight mb-6" style={{ fontFamily: 'Noto Serif JP' }}>
              <span className="flex items-baseline gap-6 whitespace-nowrap">
                <span className="text-6xl md:text-8xl font-light tracking-wider">coliving</span>
                <span className="text-2xl md:text-3xl font-light tracking-[0.3em] uppercase">HIDA TAKAYAMA</span>
              </span>
              <br />
              <span className="text-5xl md:text-6xl">Your Private Sanctuary,</span>
              <br />
              <span className="text-5xl md:text-6xl italic">Our Shared Community.</span>
            </h2>
            
            <p className="text-lg text-white/85 mb-12 max-w-xl leading-relaxed">
              Fully furnished private rooms with high-end shared amenities. 
              <br />No key money. Online application.
            </p>
            
            <a 
              href="#rooms"
              className="inline-flex items-center bg-black text-white px-8 py-4 rounded-full hover:bg-gray-800 transition-all duration-300 whitespace-nowrap"
            >
              <div className="flex items-center justify-center w-12 h-12 bg-orange-400 rounded-full mr-4">
                <i className="ri-home-3-line text-white text-xl"></i>
              </div>
              <span className="text-sm font-medium tracking-wider uppercase mr-4">Explore Rooms</span>
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Concept Section */}
      <section id="concept" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* New Concept Introduction */}
          <div className="text-center mb-20">
            <div className="text-sm font-medium text-orange-400 tracking-wider uppercase mb-4">
              Concept
            </div>
            <h3 className="text-5xl font-normal text-gray-900 mb-8 leading-tight" style={{ fontFamily: 'Noto Serif JP' }}>
              A Real Address.
              <br />
              <span className="italic">A Real First Step in Japan.</span>
            </h3>
            
            <div className="max-w-4xl mx-auto space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                <strong>coliving HIDA TAKAYAMA</strong> is a project by <strong>NPO MOSHIMOSHI</strong>,
                <br />
                created to support people who want to seriously explore life in regional Japan — not just visit.
              </p>
              
              <p>
                By staying here, you can obtain a <strong>registered address in Takayama City</strong>.
                <br />
                This makes it easier to take your next steps:
                <br />
                finding a long-term apartment, looking for work, preparing to start a business,
                <br />
                or considering long-term relocation to Japan.
              </p>
              
              <p>
                We believe you shouldn&apos;t have to decide everything at once.
                <br />
                Instead of committing immediately, we offer a place where you can <strong>live first, experience daily life, and think clearly about what comes next</strong> — all while being connected to the local community.
              </p>
              
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-xl font-medium text-gray-900">
                  coliving HIDA TAKAYAMA is not just accommodation.
                  <br />
                  <span className="text-orange-500">It is a soft landing point between short-term stay and long-term life in Japan.</span>
                </p>
              </div>
            </div>

            {/* Address Registration Notice */}
            <div className="max-w-4xl mx-auto mt-12">
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6 text-left">
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-500 rounded-full mr-4 flex-shrink-0">
                    <i className="ri-information-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      Address Registration in Japan (Important)
                    </h4>
                    <p className="text-base text-gray-700 leading-relaxed">
                      Foreign residents who receive a Residence Card are required to register their address at the local city office <strong>within 14 days</strong> after they start living at a residence. This address does not need to be a permanent or long-term home—a temporary residence is acceptable. Because our property is a residential rental for stays of one month or longer, it can be used as an address for resident registration. After registration, you are free to move later and update your address by submitting a simple change-of-address notification.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-center">
            <div className="lg:col-span-2">
              <img 
                src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/119139bb7974b0ecb4edae6df8f3e66d.jpeg"
                alt="Coliving HIDA TAKAYAMA Interior"
                className="w-full h-[600px] object-cover rounded-2xl shadow-2xl"
              />
            </div>
            
            <div className="lg:col-span-3 lg:pl-16">
              <div className="text-sm font-medium text-orange-400 tracking-wider uppercase mb-4">
                About coliving HIDA TAKAYAMA
              </div>
              
              <h3 className="text-5xl font-normal text-gray-900 mb-8 leading-tight" style={{ fontFamily: 'Noto Serif JP' }}>
                A Home Away
                <br />
                <span className="italic">From Home</span>
              </h3>
              
              <div className="space-y-5 text-base text-gray-600 leading-relaxed max-w-lg">
                <p>
                  This co-living space was created by renovating a 50-year-old Japanese house. It is primarily operated for foreigners experiencing Japan for the first time.
                </p>
                <p>
                  Through your stay here, we hope you will learn local rules and customs by immersing yourself and pave the way for long-term living in Japan. Japanese individuals seeking to live in a global environment are also welcome.
                </p>
              </div>
              
              <div className="mt-8 space-y-4">
                <div className="flex items-center p-6 bg-gray-50 rounded-xl border-l-4 border-orange-400">
                  <div className="w-6 h-6 flex items-center justify-center mr-4">
                    <i className="ri-lock-line text-orange-400"></i>
                  </div>
                  <span className="text-gray-900 font-medium">Private Rooms for Deep Work</span>
                </div>
                
                <div className="flex items-center p-6 bg-gray-50 rounded-xl border-l-4 border-orange-400">
                  <div className="w-6 h-6 flex items-center justify-center mr-4">
                    <i className="ri-team-line text-orange-400"></i>
                  </div>
                  <span className="text-gray-900 font-medium">Shared Premium Amenities</span>
                </div>
              </div>

              {/* Address & Map */}
              <div className="mt-8 p-6 bg-orange-50 rounded-xl">
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-orange-400 rounded-full mr-4">
                    <i className="ri-map-pin-line text-white"></i>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Address</h5>
                    <p className="text-gray-700">Coliving Hida Takayama, 4-60 Honmachi, Takayama City, Gifu 506-0011, Japan</p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden shadow-md mt-4">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3209.8!2d137.2526!3d36.1408!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6002a3456789abcd%3A0x1234567890abcdef!2s4+Chome+Honmachi%2C+Takayama%2C+Gifu+506-0011%2C+Japan!5e0!3m2!1sen!2sjp!4v1700000000000!5m2!1sen!2sjp"
                    width="100%"
                    height="200"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="coliving HIDA TAKAYAMA Location"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location & Lifestyle Section */}
      <section id="location" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-center">
            <div className="lg:col-span-2 space-y-8">
              <div className="text-sm font-medium text-orange-400 tracking-wider uppercase mb-4">
                Discover Takayama
              </div>
              
              <h3 className="text-5xl font-normal text-gray-900 mb-8 leading-tight" style={{ fontFamily: 'Noto Serif JP' }}>
                Conveniently Located
                <br />
                for Both Business
                <br />
                <span className="italic">&amp; Tourism</span>
              </h3>
              
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed max-w-lg">
                <p>
                  <strong>coliving HIDA TAKAYAMA</strong> is perfectly situated in the heart of Takayama. Located at Hommachi 4-chome, Takayama-shi, Gifu 506-0011.
                </p>
                
                <p>
                  A renovated 50-year-old traditional Japanese house, offering the perfect base to explore this historic region while maintaining your productivity.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center p-4 bg-orange-50 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center bg-orange-400 rounded-full mr-4">
                    <i className="ri-train-line text-white"></i>
                  </div>
                  <div>
                    <span className="text-gray-900 font-medium">Takayama Station</span>
                    <span className="text-gray-500 ml-2">12-min walk</span>
                  </div>
                </div>
                
                <div className="flex items-center p-4 bg-orange-50 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center bg-orange-400 rounded-full mr-4">
                    <i className="ri-store-2-line text-white"></i>
                  </div>
                  <div>
                    <span className="text-gray-900 font-medium">Miyagawa Morning Market</span>
                    <span className="text-gray-500 ml-2">2-min walk</span>
                  </div>
                </div>
                
                <div className="flex items-center p-4 bg-orange-50 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center bg-orange-400 rounded-full mr-4">
                    <i className="ri-restaurant-line text-white"></i>
                  </div>
                  <div>
                    <span className="text-gray-900 font-medium">Aji-to-no-ren-ichibangai</span>
                    <span className="text-gray-500 ml-2">1-min walk</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-3">
              <img 
                src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/eecc9d85a298b0d7d7a37f8da14176e2.jpeg"
                alt="Takayama Old Town"
                className="w-full h-96 object-cover object-top rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Nearby Attractions */}
      <section id="attractions" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-sm font-medium text-orange-400 tracking-wider uppercase mb-4">
              Explore the Area
            </div>
            <h3 className="text-4xl font-normal text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
              Discover <span className="italic">Takayama</span>
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="relative rounded-2xl overflow-hidden group cursor-pointer h-72">
              <img 
                src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/759ea674ca8a05305a7f54bbfc618497.jpeg"
                alt="中橋と桜"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              <div className="absolute bottom-6 left-6">
                <span className="text-white text-xl font-semibold" style={{ fontFamily: 'Noto Serif JP' }}>中橋と桜</span>
                <p className="text-white/80 text-sm mt-1">Nakabashi Bridge</p>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden group cursor-pointer h-72">
              <img 
                src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/737dabc5c9299ec159e11a78b713da05.jpeg"
                alt="春の高山祭 夜祭行列"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              <div className="absolute bottom-6 left-6">
                <span className="text-white text-xl font-semibold" style={{ fontFamily: 'Noto Serif JP' }}>春の高山祭</span>
                <p className="text-white/80 text-sm mt-1">Takayama Festival</p>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden group cursor-pointer h-72">
              <img 
                src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/9445741c53a98c2d2b2123bc185106c8.jpeg"
                alt="古い町並みと雪景色"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              <div className="absolute bottom-6 left-6">
                <span className="text-white text-xl font-semibold" style={{ fontFamily: 'Noto Serif JP' }}>古い町並みと雪景色</span>
                <p className="text-white/80 text-sm mt-1">Old Town in Snow</p>
              </div>
            </div>
          </div>

          {/* Horizontal Scrollable Gallery */}
          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-2xl font-normal text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
                Photo <span className="italic">Gallery</span>
              </h4>
              <div className="flex items-center text-sm text-gray-500">
                <i className="ri-arrow-left-right-line mr-2"></i>
                <span>Swipe to explore</span>
              </div>
            </div>
            <div className="overflow-x-auto scrollbar-hide pb-4 -mx-6 px-6" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
              <div className="flex gap-4" style={{ width: 'max-content' }}>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/17gTqEras8gOI1zbBpv9MECJhdDXhORLy" alt="古い町並（春）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1VfK9BJy5DewMUPfA0P9GT8sErgFEZU2H" alt="栃尾温泉 蛍の湯" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1dq21k1EnIutYSmgSrXO3aR8EOVd9tO2u" alt="平湯温泉 平湯の湯" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1tTQMfo3hjFIqJqN7ZepZwDSUD9SUKpns" alt="ほおのき平スキー場" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1wKpSWwtUXqLbhzKg8-iiYXfFwmnOxPYo" alt="みたらし団子" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1xrntOIBudwCoFMtQp-NLDGpUxRbTYa-l" alt="飛騨牛" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1Bddq9DVxpqA35rfNAWgVU5o7jYdWZu4K" alt="山野草花園 クリンソウ" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1-RRIq5HYfFa4Uzff2G1T4scvpDKiHR2K" alt="古い町並（夏）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/14gNUSvsmz9AFfn8uKEQtETSfq3Wm1huq" alt="古い町並（夏）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1NSkh8UsuDgpbhA7DEiCajE81BlISPAHI" alt="一位一刀彫" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1DvRXstqWLUOIVkaxejyxM6m1C0SG67DG" alt="渋草焼" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1RVf8BW-ciePufYM7YYSZGULVQZOthoIP" alt="城山公園" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1Ne8AJNqH0qt0eTh-shwKWUvbtEKtK3s9" alt="江名子川（秋）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/16Gene9x8m9FkV5X22KN3haqeTX2nWPaS" alt="御母衣ダム" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1RRF5Y_qv-Mm56pDgWUn9j5MBvKrO40tu" alt="江名子川（春）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1THXkR_33ZfZV-ALVIp5A2rMctF_NhaCS" alt="宮川緑地公園" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1AXRMYHgmCYqXpsugSnX65gywLVq4Weec" alt="青屋神明神社" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1ZC9Gc0DHgF7mw8WXyXXz716xcLQXqopX" alt="青屋神明神社" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/11t2-C3M1PErBxYocPm4poEcoSBuLgILp" alt="浅井神明神社" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1uJe4wrifqFDUHqFtYAKajPptV5JJMh5B" alt="カクレハ高原" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/15DAga8Wnrzd5r95HWBWXPoFnYwjxQWG6" alt="国府町全景" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1KL7s9sSE8lUvi342xPxo_UOdwzx8eSpG" alt="乗鞍岳" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1ggEReBDvZ7rKekJOEbK-Zuzd3-wERHYA" alt="乗鞍岳" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/157UtUX2hyMSHJSzxWBwqHI8q7eUPRMpn" alt="新穂高ロープウェイ" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1MLKdAyNmaaQ9jcNSOpKRRwbsmz8N3DM8" alt="赤い中橋（春）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1s1ijWLV1c6Cg7psINB8tGvxKf20EC88p" alt="赤い中橋（春）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1e2WHmMgNjswGZaUGLM2su3t1ai8pgNaf" alt="赤い中橋（秋）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1kZa-h1UUiAXhuRlec0KhA-TgvcFiCO-A" alt="赤い中橋（秋）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/10RZgfdmSYfqQ2IHwbgww-S3--yi_lIDi" alt="赤い中橋（秋）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1MCmFYdDwbzsRxiNZFX04JoJOW2j9P7Wi" alt="朝市" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/170gDnKb62hElwC8eyLWxZ1ZXXxEEc8tx" alt="朝市" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1CU4FSwNkkjv-HBO-jxz8GZK6vHdcRMu9" alt="飛騨の里" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1dc2VAJhTta_hI5mfHtzeinr8KWI--BgR" alt="飛騨の里（秋）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1lPzkN2EWzQTbjMxmMdB20R00fjpawjRz" alt="飛騨の里（冬）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1wefKspugOeqJ5JgoU5pKAN3AjKYCY0KV" alt="飛騨の里ひな祭り" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1lPEtnY3c3hPLKCb2AZvZk_JraJDHNhBq" alt="日下部民藝館" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/13w3JKGe-YLAO1K76jlV1Hhp5BdK2pWTO" alt="吉島家" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1atWehMfIVTAh7L1YzbGjuDcTsB0Zl3M1" alt="宮地家住宅" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1iwuqudSLVhOJvoKZVr96PI5ysJySIOBN" alt="高山陣屋" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1AYFyE-ZKwBaMOXi8h8bHGiJzmzN4c3aa" alt="鍛冶橋 足長像" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1n5-hRx37cWZQh9gwKU-efpTUBG1RAQ5-" alt="清峰寺" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1k87vdDfrnF95blszfQewyLUnDDBvUMME" alt="東山遊歩道（夏）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/11agK_7SPNGbsPYH_1GhfQVorylhvEJKz" alt="春の高山祭" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/10Mp3pEeO5xlo3LUzv5pLPzEWUB75qyGC" alt="春の高山祭" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1NNsmf3eeQQE5ZFrxMNKuoeUwtH7QsyVF" alt="秋の高山祭" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1E4aIXe6t9s_2R6Qnd7OJudJWokSwcMW7" alt="秋の高山祭" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1_XIIuPUOb0MgMSDKVIPu-_td7Sk77p16" alt="秋の高山祭" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1FkJbFt-dGMIpk0wkWy8PqgFXdhhi92Gk" alt="宇津江四十八滝（夏）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1H5kIFDN3ZjEPQVN1t0HPMkwlzdKGR1zL" alt="市政記念館" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1aFvNExXk7sVM9P6yQUP8j39AlXscsaT3" alt="高山陣屋" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1lpU9nR_0rxYsmBmqoudj3TRV461kmbTk" alt="高山陣屋" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1xG-l8H2VnkF_H_UgPgrShyyn31CG6_pj" alt="城山公園" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1C5bz0kvHbF7vTxpJq44TkuxQCOrUdFlR" alt="大原カタクリ群生地" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1vMxGBBUNBSCCeNOR-UYrrZvKejROkt_U" alt="天照寺" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/179sYtkaVfjOphZqef1T6Ok1EVzmqKbPk" alt="日枝神社" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1KwLOq3jlTZGGNhI3p7IkcnsC33RB9Bep" alt="古い町並（夏）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1y5-P4wOywoFoNl4Ncds75vSN7XbB-iPU" alt="古い町並（夜）" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1KwnFM2PIbdRq9_9jWaffJwFNTOca0tKv" alt="高山ラーメン" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1HO4ATCsmjfoj5JK7CRPZW42PSjwfVVbK" alt="桜山風鈴まつり" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1hq_hKaL25Yz2n-FJDD-jVWCUMvrYu39z" alt="桜山風鈴まつり" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1HA2jhz0a-n8SwFapV3a19eEeHH2zmjTF" alt="飛騨高山手筒花火打上げ" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/1496ARmPqUsosXT6hJWivNmVMBDU7LV0j" alt="中尾かまくらまつり" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="w-64 h-48 flex-shrink-0 rounded-xl overflow-hidden">
                  <img src="https://lh3.googleusercontent.com/d/10rpRFVoicXn8zuXh4sg7lBopuCQTnTyz" alt="中尾かまくらまつり" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Choose Your Room Section */}
      <section id="rooms" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <div className="text-sm font-medium text-orange-400 tracking-wider uppercase mb-4">
              Find Your Space
            </div>
            <h3 className="text-6xl font-normal text-gray-900 mb-6" style={{ fontFamily: 'Noto Serif JP' }}>
              Choose Your
              <br />
              <span className="italic">Perfect Room</span>
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              5 rooms with a total capacity of 8 residents. A renovated 50-year-old traditional Japanese house.
            </p>
            <div className="mt-6 inline-flex items-center px-6 py-3 bg-orange-50 rounded-xl text-sm text-gray-700">
              <i className="ri-money-yen-circle-line text-orange-400 mr-2"></i>
              Initial cost: 1 month&apos;s rent (No key money / No deposit)
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Room 101 */}
            <div id="room-101" className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group" data-product-shop>
              <div className="relative h-80">
                <img 
                  src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/c8f6806b25fa021259dcbb94ed243416.jpeg"
                  alt="Room 101"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">Available</span>
                </div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-gray-900/70 text-white text-sm font-medium rounded-full">1F</span>
                </div>
              </div>
              
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
                    Room 101
                  </h4>
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">7.2㎡</span>
                </div>
                
                <div className="space-y-3 mb-6 text-sm text-gray-600">
                  <div className="flex items-center">
                    <i className="ri-user-line text-orange-400 mr-3"></i>
                    <span>Capacity: 1 person</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-hotel-bed-line text-orange-400 mr-3"></i>
                    <span>Single Bed</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-lock-line text-orange-400 mr-3"></i>
                    <span>Padlock: ○</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-archive-line text-gray-300 mr-3"></i>
                    <span>Closet: None</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-temp-cold-line text-orange-400 mr-3"></i>
                    <span>Air Conditioning: ○</span>
                  </div>
                </div>
                
                <div className="mb-4">
                  <span className="text-sm text-gray-500">Base Rent</span>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">¥50,000</span>
                    <span className="text-sm text-gray-500 ml-1">/month</span>
                  </div>
                </div>

                <div className="mb-6 bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 font-medium mb-2">Utility Fee by Season</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                    <span>Apr-Jun, Oct-Nov:</span><span className="text-right font-medium">¥4,000</span>
                    <span>Jul-Sep, Mar:</span><span className="text-right font-medium">¥6,000</span>
                    <span>Dec-Feb:</span><span className="text-right font-medium">¥8,000</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">* Auto-renewed annually</p>
                </div>
                
                <a href="/availability?room=room-101" className="w-full bg-gray-900 text-white py-3 px-6 rounded-xl hover:bg-orange-400 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center">
                  <span className="font-medium">Check Availability</span>
                  <i className="ri-arrow-right-line ml-3"></i>
                </a>
              </div>
            </div>

            {/* Room 102 - Women Only */}
            <div id="room-102" className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group" data-product-shop>
              <div className="relative h-80">
                <img 
                  src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/f8c2908a24289559bcf01c667dc1d2d5.jpeg"
                  alt="Room 102"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">Available</span>
                </div>
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="px-3 py-1 bg-gray-900/70 text-white text-sm font-medium rounded-full">1F</span>
                </div>
              </div>
              
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
                    Room 102
                  </h4>
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">7.2㎡</span>
                </div>
                
                <div className="space-y-3 mb-6 text-sm text-gray-600">
                  <div className="flex items-center">
                    <i className="ri-user-line text-orange-400 mr-3"></i>
                    <span>Capacity: 2 persons</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-hotel-bed-line text-orange-400 mr-3"></i>
                    <span>Bunk Bed</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-lock-line text-orange-400 mr-3"></i>
                    <span>Padlock: ○</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-archive-line text-orange-400 mr-3"></i>
                    <span>Closet: Built-in</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-temp-cold-line text-orange-400 mr-3"></i>
                    <span>Air Conditioning: ○</span>
                  </div>
                </div>
                
                <div className="mb-4">
                  <span className="text-sm text-gray-500">Base Rent</span>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">¥55,000</span>
                    <span className="text-sm text-gray-500 ml-1">/month</span>
                  </div>
                </div>

                <div className="mb-6 bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 font-medium mb-2">Utility Fee by Season</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                    <span>Apr-Jun, Oct-Nov:</span><span className="text-right font-medium">¥8,000</span>
                    <span>Jul-Sep, Mar:</span><span className="text-right font-medium">¥12,000</span>
                    <span>Dec-Feb:</span><span className="text-right font-medium">¥16,000</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">* Auto-renewed annually</p>
                </div>
                
                <a href="/availability?room=room-102" className="w-full bg-gray-900 text-white py-3 px-6 rounded-xl hover:bg-orange-400 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center">
                  <span className="font-medium">Check Availability</span>
                  <i className="ri-arrow-right-line ml-3"></i>
                </a>
              </div>
            </div>

            {/* Room 201 */}
            <div id="room-201" className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group" data-product-shop>
              <div className="relative h-80">
                <img 
                  src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/56b4f14ca51f8054b95df869d16e9d03.jpeg"
                  alt="Room 201"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">Available</span>
                </div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-gray-900/70 text-white text-sm font-medium rounded-full">2F</span>
                </div>
              </div>
              
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
                    Room 201
                  </h4>
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">9.6㎡</span>
                </div>
                
                <div className="space-y-3 mb-6 text-sm text-gray-600">
                  <div className="flex items-center">
                    <i className="ri-user-line text-orange-400 mr-3"></i>
                    <span>Capacity: 2 persons</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-hotel-bed-line text-orange-400 mr-3"></i>
                    <span>Bunk Bed</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-lock-line text-orange-400 mr-3"></i>
                    <span>Padlock: ○</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-archive-line text-orange-400 mr-3"></i>
                    <span>Closet: Built-in</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-temp-cold-line text-orange-400 mr-3"></i>
                    <span>Air Conditioning: ○</span>
                  </div>
                </div>
                
                <div className="mb-4">
                  <span className="text-sm text-gray-500">Base Rent</span>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">¥60,000</span>
                    <span className="text-sm text-gray-500 ml-1">/month</span>
                  </div>
                </div>

                <div className="mb-6 bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 font-medium mb-2">Utility Fee by Season</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                    <span>Apr-Jun, Oct-Nov:</span><span className="text-right font-medium">¥8,000</span>
                    <span>Jul-Sep, Mar:</span><span className="text-right font-medium">¥12,000</span>
                    <span>Dec-Feb:</span><span className="text-right font-medium">¥16,000</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">* Auto-renewed annually</p>
                </div>
                
                <a href="/availability?room=room-201" className="w-full bg-gray-900 text-white py-3 px-6 rounded-xl hover:bg-orange-400 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center">
                  <span className="font-medium">Check Availability</span>
                  <i className="ri-arrow-right-line ml-3"></i>
                </a>
              </div>
            </div>

            {/* Room 202 */}
            <div id="room-202" className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group" data-product-shop>
              <div className="relative h-80">
                <img 
                  src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/c666a9e8d86b1416c0a8cc1cc30f3f77.jpeg"
                  alt="Room 202"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">Available</span>
                </div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-gray-900/70 text-white text-sm font-medium rounded-full">2F</span>
                </div>
              </div>
              
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
                    Room 202
                  </h4>
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">7.2㎡</span>
                </div>
                
                <div className="space-y-3 mb-6 text-sm text-gray-600">
                  <div className="flex items-center">
                    <i className="ri-user-line text-orange-400 mr-3"></i>
                    <span>Capacity: 2 persons</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-hotel-bed-line text-orange-400 mr-3"></i>
                    <span>Bunk Bed</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-lock-line text-gray-300 mr-3"></i>
                    <span>Padlock: ×</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-archive-line text-orange-400 mr-3"></i>
                    <span>Closet: 1/4</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-temp-cold-line text-orange-400 mr-3"></i>
                    <span>Air Conditioning: ○</span>
                  </div>
                </div>
                
                <div className="mb-4">
                  <span className="text-sm text-gray-500">Base Rent</span>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">¥45,000</span>
                    <span className="text-sm text-gray-500 ml-1">/month</span>
                  </div>
                </div>

                <div className="mb-6 bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 font-medium mb-2">Utility Fee by Season</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                    <span>Apr-Jun, Oct-Nov:</span><span className="text-right font-medium">¥8,000</span>
                    <span>Jul-Sep, Mar:</span><span className="text-right font-medium">¥12,000</span>
                    <span>Dec-Feb:</span><span className="text-right font-medium">¥16,000</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">* Auto-renewed annually</p>
                </div>
                
                <a href="/availability?room=room-202" className="w-full bg-gray-900 text-white py-3 px-6 rounded-xl hover:bg-orange-400 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center">
                  <span className="font-medium">Check Availability</span>
                  <i className="ri-arrow-right-line ml-3"></i>
                </a>
              </div>
            </div>

            {/* Room 203 */}
            <div id="room-203" className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group" data-product-shop>
              <div className="relative h-80">
                <img 
                  src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/4de45fa602a26fb7767fed4a441c1ac2.jpeg"
                  alt="Room 203"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">Available</span>
                </div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-gray-900/70 text-white text-sm font-medium rounded-full">2F</span>
                </div>
              </div>
              
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
                    Room 203
                  </h4>
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">4.9㎡</span>
                </div>
                
                <div className="space-y-3 mb-6 text-sm text-gray-600">
                  <div className="flex items-center">
                    <i className="ri-user-line text-orange-400 mr-3"></i>
                    <span>Capacity: 1 person</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-hotel-bed-line text-orange-400 mr-3"></i>
                    <span>Closet Bed</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-lock-line text-gray-300 mr-3"></i>
                    <span>Padlock: ×</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-archive-line text-orange-400 mr-3"></i>
                    <span>Closet: 1/2</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-temp-cold-line text-orange-400 mr-3"></i>
                    <span>Air Conditioning: ○</span>
                  </div>
                </div>
                
                <div className="mb-4">
                  <span className="text-sm text-gray-500">Base Rent</span>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">¥35,000</span>
                    <span className="text-sm text-gray-500 ml-1">/month</span>
                  </div>
                </div>

                <div className="mb-6 bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 font-medium mb-2">Utility Fee by Season</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                    <span>Apr-Jun, Oct-Nov:</span><span className="text-right font-medium">¥4,000</span>
                    <span>Jul-Sep, Mar:</span><span className="text-right font-medium">¥6,000</span>
                    <span>Dec-Feb:</span><span className="text-right font-medium">¥8,000</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">* Auto-renewed annually</p>
                </div>
                
                <a href="/availability?room=room-203" className="w-full bg-gray-900 text-white py-3 px-6 rounded-xl hover:bg-orange-400 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center">
                  <span className="font-medium">Check Availability</span>
                  <i className="ri-arrow-right-line ml-3"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Floor Plan Section */}
      <section id="floor-plans" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-sm font-medium text-orange-400 tracking-wider uppercase mb-4">
              Floor Plans
            </div>
            <h3 className="text-5xl font-normal text-gray-900 mb-6" style={{ fontFamily: 'Noto Serif JP' }}>
              Explore Our
              <br />
              <span className="italic">Layout</span>
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Detailed floor plans for both 1st and 2nd floors. See where each room and amenity is located.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* 1F Floor Plan */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-3xl font-semibold text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
                  1st Floor
                </h4>
                <span className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-full">1F</span>
              </div>
              <div className="bg-white rounded-xl overflow-hidden shadow-md aspect-[4/3] flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/f13080c42f22c43008aa5bfb36e58395.jpeg"
                  alt="1st Floor Plan"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="mt-6 space-y-3 text-sm text-gray-600">
                <a href="#room-101" className="flex items-center hover:text-orange-500 transition-colors cursor-pointer group">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 group-hover:scale-125 transition-transform"></div>
                  <span>Room 101 (Single)</span>
                  <i className="ri-arrow-right-s-line ml-auto opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </a>
                <a href="#room-102" className="flex items-center hover:text-orange-500 transition-colors cursor-pointer group">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 group-hover:scale-125 transition-transform"></div>
                  <span>Room 102 (Bunk Bed - Women Only)</span>
                  <i className="ri-arrow-right-s-line ml-auto opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </a>
                <div className="flex flex-wrap items-center gap-x-1">
                  <div className="w-2 h-2 bg-gray-300 rounded-full mr-3"></div>
                  <a href="#living-room" className="hover:text-orange-500 transition-colors cursor-pointer">Living Room</a>
                  <span>,</span>
                  <a href="#kitchen" className="hover:text-orange-500 transition-colors cursor-pointer">Kitchen</a>
                  <span>,</span>
                  <a href="#shower-rooms" className="hover:text-orange-500 transition-colors cursor-pointer">Shower Rooms</a>
                  <span>,</span>
                  <a href="#toilet" className="hover:text-orange-500 transition-colors cursor-pointer">Toilet</a>
                  <span>,</span>
                  <a href="#wash-basin" className="hover:text-orange-500 transition-colors cursor-pointer">Wash Basin</a>
                </div>
              </div>
            </div>

            {/* 2F Floor Plan */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-3xl font-semibold text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
                  2nd Floor
                </h4>
                <span className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-full">2F</span>
              </div>
              <div className="bg-white rounded-xl overflow-hidden shadow-md aspect-[4/3] flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/606d81751137d1a6ff64abec6e48601e.jpeg"
                  alt="2nd Floor Plan"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="mt-6 space-y-3 text-sm text-gray-600">
                <a href="#room-201" className="flex items-center hover:text-orange-500 transition-colors cursor-pointer group">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 group-hover:scale-125 transition-transform"></div>
                  <span>Room 201 (Bunk Bed)</span>
                  <i className="ri-arrow-right-s-line ml-auto opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </a>
                <a href="#room-202" className="flex items-center hover:text-orange-500 transition-colors cursor-pointer group">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 group-hover:scale-125 transition-transform"></div>
                  <span>Room 202 (Bunk Bed)</span>
                  <i className="ri-arrow-right-s-line ml-auto opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </a>
                <a href="#room-203" className="flex items-center hover:text-orange-500 transition-colors cursor-pointer group">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 group-hover:scale-125 transition-transform"></div>
                  <span>Room 203 (Closet Bed)</span>
                  <i className="ri-arrow-right-s-line ml-auto opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </a>
                <div className="flex flex-wrap items-center gap-x-1">
                  <div className="w-2 h-2 bg-gray-300 rounded-full mr-3"></div>
                  <a href="#living-room" className="hover:text-orange-500 transition-colors cursor-pointer">Living Room</a>
                  <span>,</span>
                  <a href="#toilet" className="hover:text-orange-500 transition-colors cursor-pointer">Toilet</a>
                  <span>,</span>
                  <a href="#wash-basin" className="hover:text-orange-500 transition-colors cursor-pointer">Wash Basin</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shared Amenities Section */}
      <section id="amenities" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-sm font-medium text-orange-400 tracking-wider uppercase mb-4">
              Premium Shared Spaces
            </div>
            <h3 className="text-4xl font-normal text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
              Amenities That Feel Like
              <br />
              <span className="italic">Your Own</span>
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Fully equipped kitchen, shared living space, and modern bathroom facilities.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {/* Living Room */}
            <div id="living-room">
              <div className="mb-8">
                <img 
                  src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/541890d8df28bdb03e98c5bbe8863865.jpeg"
                  alt="Living Room"
                  className="w-full h-80 object-cover object-top rounded-2xl shadow-lg"
                />
              </div>
              <h4 className="text-3xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Noto Serif JP' }}>
                Living Room
              </h4>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Comfortable shared living space on each floor. A place to relax, socialize, and unwind.
              </p>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  One per Floor (1F &amp; 2F) — Total 2
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  Shared Cleaning Rotation
                </li>
              </ul>
            </div>

            {/* Kitchen */}
            <div id="kitchen">
              <div className="mb-8">
                <img 
                  src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/9add98d00671104e0f8b745f4cbeb75c.jpeg"
                  alt="Kitchen"
                  className="w-full h-80 object-cover object-top rounded-2xl shadow-lg"
                />
              </div>
              <h4 className="text-3xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Noto Serif JP' }}>
                Kitchen
              </h4>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Fully equipped kitchen with premium appliances. Residents share cleaning responsibilities.
              </p>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  Gas Stove
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  Microwave
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  Toaster
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  Refrigerator
                </li>
              </ul>
            </div>

            {/* Shower Rooms */}
            <div id="shower-rooms">
              <div className="mb-8">
                <img 
                  src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/d40b7b1f1d43146fe8fbee5c3b89077a.jpeg"
                  alt="Shower Rooms"
                  className="w-full h-80 object-cover object-top rounded-2xl shadow-lg"
                />
              </div>
              <h4 className="text-3xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Noto Serif JP' }}>
                Shower Rooms
              </h4>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Private shower stalls with modern fixtures. Residents maintain cleanliness together.
              </p>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  2 Shower Rooms
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  Hot Water 24/7
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  Shared Cleaning Rotation
                </li>
              </ul>
            </div>

            {/* Toilets */}
            <div id="toilet">
              <div className="mb-8 w-full h-80 bg-gray-200 rounded-2xl shadow-lg flex items-center justify-center">
                <i className="ri-door-closed-line text-6xl text-gray-400"></i>
              </div>
              <h4 className="text-3xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Noto Serif JP' }}>
                Toilets
              </h4>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Clean toilet facilities on each floor. Residents take turns keeping them tidy.
              </p>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  1 per Floor (1F &amp; 2F) — Total 2
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  Cleaning Rotation System
                </li>
              </ul>
            </div>

            {/* Wash Basin */}
            <div id="wash-basin">
              <div className="mb-8">
                <img 
                  src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/f094e882037705c7d72ad4f2c1acc1fd.jpeg"
                  alt="Wash Basin"
                  className="w-full h-80 object-cover object-top rounded-2xl shadow-lg"
                />
              </div>
              <h4 className="text-3xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Noto Serif JP' }}>
                Wash Basin
              </h4>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Shared wash basin on each floor for daily grooming and hygiene.
              </p>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  1 per Floor (1F &amp; 2F) — Total 2
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  Shared Cleaning Rotation
                </li>
              </ul>
            </div>

            {/* Laundry */}
            <div id="laundry">
              <div className="mb-8">
                <img 
                  src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/4ab543043714db81d736d076ce1f0ede.jpeg"
                  alt="Laundry"
                  className="w-full h-80 object-cover object-top rounded-2xl shadow-lg"
                />
              </div>
              <h4 className="text-3xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Noto Serif JP' }}>
                Laundry
              </h4>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Washing machine available for residents. Shared cleaning responsibility.
              </p>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  1 Washing Machine
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                  Shared Cleaning Rotation
                </li>
              </ul>
            </div>
          </div>

          {/* Annex Building */}
          <div className="mt-20 bg-white rounded-2xl p-10 shadow-lg">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-xl mr-4">
                <i className="ri-building-2-line text-orange-500 text-xl"></i>
              </div>
              <div>
                <h4 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
                  Other benefits for residents
                </h4>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex items-start p-5 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 flex items-center justify-center bg-orange-400 rounded-full mr-4 flex-shrink-0">
                  <i className="ri-sofa-line text-white"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">Shared Lounge_Backpackers Hida Takayama</h5>
                  <p className="text-sm text-gray-600">Relaxation and socializing space for residents</p>
                  <img 
                    src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/7b6ffd67b253237f401ded4eb9cac13a.jpeg"
                    alt="Shared Lounge"
                    className="w-full h-40 object-cover object-top rounded-lg mt-3"
                  />
                </div>
              </div>
              
              <div className="flex items-start p-5 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 flex items-center justify-center bg-orange-400 rounded-full mr-4 flex-shrink-0">
                  <i className="ri-macbook-line text-white"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">Coworking Access</h5>
                  <p className="text-sm text-gray-600">60 hours/month at Backpackers Hida Takayama 1F coworking space</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Book Section */}
      <section id="booking" className="py-32 bg-green-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <div className="text-sm font-medium text-orange-400 tracking-wider uppercase mb-4">
              How It Works
            </div>
            <h3 className="text-5xl font-normal text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Noto Serif JP' }}>
              Living in Hida Takayama,
              <br />
              <span className="italic">made simple.</span>
            </h3>
            <div className="max-w-3xl mx-auto">
              <p className="text-xl text-gray-700 leading-relaxed">
                Everything online.
                <br />
                No guarantor. No paperwork.
                <br />
                <strong className="text-orange-500">Your first address in Japan.</strong>
              </p>
            </div>
          </div>
          
          <div className="max-w-5xl mx-auto space-y-12">
            {/* Step 1 */}
            <div className="relative pl-20">
              <div className="absolute left-0 top-0 w-14 h-14 bg-orange-400 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                1
              </div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-3">Apply for an Online Meeting</h4>
              <p className="text-gray-700 leading-relaxed">
                Please start by submitting an online meeting request through our booking form.
                <br />
                This is the first step for all applications.
                <br />
                <strong className="text-orange-500">No walk-ins or direct applications.</strong>
              </p>
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Step 2 */}
            <div className="relative pl-20">
              <div className="absolute left-0 top-0 w-14 h-14 bg-orange-400 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                2
              </div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-3">Online Meeting &amp; Pre-Check</h4>
              <p className="text-gray-700 leading-relaxed mb-3">
                During the online meeting, please tell us about:
              </p>
              <ul className="space-y-2 text-gray-700 ml-4">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>Your preferred room or viewing request</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>Your plans and purpose of stay</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>Desired stay period and schedule</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                We&apos;ll check availability and suitability together.
              </p>
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Step 3 */}
            <div className="relative pl-20">
              <div className="absolute left-0 top-0 w-14 h-14 bg-orange-400 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                3
              </div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-3">Online Application (After Meeting)</h4>
              <p className="text-gray-700 leading-relaxed mb-3">
                After the meeting, we will send you a designated application form by email.
              </p>
              <p className="text-gray-700 leading-relaxed mb-2">Please submit:</p>
              <ul className="space-y-2 text-gray-700 ml-4">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>Passport</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>Residence Card (在留カード)</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>Completed online application form</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                <strong className="text-orange-500">No guarantor is required.</strong>
              </p>
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Step 4 */}
            <div className="relative pl-20">
              <div className="absolute left-0 top-0 w-14 h-14 bg-orange-400 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                4
              </div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-3">Screening &amp; Approval</h4>
              <p className="text-gray-700 leading-relaxed">
                We review your application and documents.
                <br />
                Screening usually takes 1–3 business days.
                <br />
                <br />
                Once approved, we proceed to the contract.
              </p>
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Step 5 */}
            <div className="relative pl-20">
              <div className="absolute left-0 top-0 w-14 h-14 bg-orange-400 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                5
              </div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-3">Online Contract</h4>
              <p className="text-gray-700 leading-relaxed mb-3">
                Sign the rental agreement online.
              </p>
              <ul className="space-y-2 text-gray-700 ml-4">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>No printing</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>No stamps</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>No postal mail</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                Everything is completed digitally.
              </p>
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Step 6 */}
            <div className="relative pl-20">
              <div className="absolute left-0 top-0 w-14 h-14 bg-orange-400 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                6
              </div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-3">Payment (Credit Card Only)</h4>
              <p className="text-gray-700 leading-relaxed">
                After signing, payment is completed by credit card only.
                <br />
                <br />
                Your booking is finalized once payment is confirmed.
              </p>
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Step 7 */}
            <div className="relative pl-20">
              <div className="absolute left-0 top-0 w-14 h-14 bg-orange-400 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                7
              </div>
              <h4 className="text-2xl font-semibold text-gray-900 mb-3">Move-in & Key Access</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span><strong>Timing:</strong> On your move-in date</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span><strong>Rooms with padlocks:</strong> Keys will be provided at move-in</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span><strong>On-site support:</strong> We'll guide you through the share house rules and show you around</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                Our staff will be there to support you and ensure a smooth move-in experience.
              </p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="text-center mt-16 space-y-4">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link 
                to="/availability"
                className="bg-gray-900 text-white px-12 py-4 rounded-full hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center"
              >
                <i className="ri-calendar-check-line mr-3"></i>
                <span className="font-medium text-lg tracking-wider uppercase">Book an Online Meeting</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-sm font-medium text-orange-400 tracking-wider uppercase mb-4">
              Support
            </div>
            <h3 className="text-4xl font-normal text-gray-900 mb-4" style={{ fontFamily: 'Noto Serif JP' }}>
              Frequently Asked
              <br />
              <span className="italic">Questions</span>
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about living at coliving HIDA TAKAYAMA.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-12">
            {faqData.map((item, index) => (
              <FaqAccordionItem
                key={index}
                item={item}
                isOpen={openFaqIndex === index}
                onToggle={() =>
                  setOpenFaqIndex(openFaqIndex === index ? null : index)
                }
              />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-sm font-medium text-orange-400 tracking-wider uppercase mb-4">
              Get in Touch
            </div>
            <h3 className="text-4xl font-normal text-gray-900 mb-4" style={{ fontFamily: 'Noto Serif JP' }}>
              Questions or
              <br />
              <span className="italic">Requests?</span>
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Feel free to reach out with any questions, special requests, or inquiries about your stay.
            </p>
          </div>

          <div className="text-center">
            <button 
              onClick={() => setShowContactForm(true)}
              className="bg-gray-900 text-white px-12 py-4 rounded-full hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-send-line mr-3"></i>
              <span className="font-medium text-lg tracking-wider uppercase">Send a Message</span>
            </button>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              For urgent inquiries, please email us directly at{' '}
              <a href="mailto:info@moshimoshi.info" className="text-orange-500 hover:underline cursor-pointer">
                info@moshimoshi.info
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* About MOSHI MOSHI Section */}
      <section id="moshimoshi" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-8">
              <img 
                src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/cbe4e2bc00653e83f18306ef9980b476.jpeg"
                alt="NPO MOSHI MOSHI Logo"
                className="w-32 h-32 object-contain"
              />
            </div>
            <div className="text-sm font-medium text-orange-400 tracking-wider uppercase mb-4">
              Operated by
            </div>
            <h3 className="text-4xl font-normal text-gray-900 mb-4" style={{ fontFamily: 'Noto Serif JP' }}>
              NPO <span className="italic">MOSHI MOSHI</span>
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              MOSHI MOSHI supports digital nomads who are captivated by Takayama City and work using IT while traveling the world.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="p-6 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-xl mb-5">
                <i className="ri-global-line text-orange-500 text-xl"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Noto Serif JP' }}>
                <a href="#" className="hover:text-orange-400 transition-colors cursor-pointer">Inbound Business Consulting</a>
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                We promote consultations, exchange meetings, and human resource development for multicultural understanding. We build the foundation for people and communities worldwide to become &quot;good neighbors.&quot;
              </p>
            </div>

            <div className="p-6 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-xl mb-5">
                <i className="ri-home-heart-line text-orange-500 text-xl"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Noto Serif JP' }}>
                <a href="#" className="hover:text-orange-400 transition-colors cursor-pointer">Digital Nomads &amp; Coliving</a>
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                For digital nomads seeking mid-to-long-term stays in Takayama, we provide co-living management and lifestyle support. We connect the region&apos;s charm with a comfortable work-life environment.
              </p>
            </div>

            <div className="p-6 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-xl mb-5">
                <i className="ri-briefcase-line text-orange-500 text-xl"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Noto Serif JP' }}>
                <a href="#" className="hover:text-orange-400 transition-colors cursor-pointer">Web Job Advertisements</a>
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Comprehensive support for corporate globalization and hiring international talent. Connecting global talent with Japanese companies through web advertising.
              </p>
            </div>

            <div className="p-6 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-xl mb-5">
                <i className="ri-graduation-cap-line text-orange-500 text-xl"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Noto Serif JP' }}>
                <a href="#" className="hover:text-orange-400 transition-colors cursor-pointer">International Internship Program</a>
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                To foster international talent exchange and cultivate next-generation leaders, we bring young professionals from around the world to local companies. We promote co-creation through hands-on experience.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex items-center space-x-6 text-sm text-gray-500">
              <a href="https://moshimoshi.info" rel="nofollow" className="flex items-center hover:text-orange-400 transition-colors cursor-pointer">
                <i className="ri-global-line mr-2"></i>
                moshimoshi.info
              </a>
              <a href="mailto:info@moshimoshi.info" className="flex items-center hover:text-orange-400 transition-colors cursor-pointer">
                <i className="ri-mail-line mr-2"></i>
                info@moshimoshi.info
              </a>
              <a href="https://www.instagram.com/moshi_moshi_hida/" rel="nofollow" className="flex items-center hover:text-orange-400 transition-colors cursor-pointer">
                <i className="ri-instagram-line mr-2"></i>
                Instagram
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-orange-50 text-gray-800 relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Brand Column */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-8">
                <img 
                  src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/f0377fb5ec1076e4cb9e676fd4f0df74.jpeg" 
                  alt="Coliving HIDA TAKAYAMA"
                  className="w-8 h-8 rounded-full object-cover"
                />
                <h5 className="text-2xl font-semibold whitespace-nowrap text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
                  coliving HIDA TAKAYAMA<span className="text-orange-400">.</span>
                </h5>
              </div>
              
              <div className="text-2xl font-light leading-tight mb-6 text-gray-900" style={{ fontFamily: 'Noto Serif JP' }}>
                Live, Work, Thrive
                <br />
                in the Heart of Japan
              </div>
              
              <div className="text-sm text-gray-500 leading-relaxed">
                <p className="font-medium text-gray-800">Operated by NPO MOSHI MOSHI</p>
                <p className="mt-2">3F Paulownia Building,<br />3-58-11 Honmachi, Takayama-shi, Gifu 506-0011, Japan</p>
                <div className="mt-3 flex items-center space-x-4">
                  <a href="https://" rel="nofollow" className="hover:text-orange-500 transition-colors cursor-pointer">
                    <i className="ri-global-line mr-1"></i>moshimoshi.info
                  </a>
                  <a href="https://" className="hover:text-orange-500 transition-colors cursor-pointer">
                    <i className="ri-mail-line mr-1"></i>info@moshimoshi.info
                  </a>
                </div>
              </div>
            </div>
            
            {/* Navigation Columns */}
            <div>
              <h6 className="text-gray-900 font-semibold uppercase tracking-wider text-sm mb-6">Rooms</h6>
              <div className="space-y-4 text-gray-600">
                <a href="#rooms" className="block hover:text-orange-500 transition-colors cursor-pointer">Room Types</a>
                <a href="#rooms" className="block hover:text-orange-500 transition-colors cursor-pointer">Availability</a>
                <a href="#rooms" className="block hover:text-orange-500 transition-colors cursor-pointer">Pricing</a>
              </div>
            </div>
            
            <div>
              <h6 className="text-gray-900 font-semibold uppercase tracking-wider text-sm mb-6">Amenities</h6>
              <div className="space-y-4 text-gray-600">
                <a href="#amenities" className="block hover:text-orange-500 transition-colors cursor-pointer">Kitchen</a>
                <a href="#amenities" className="block hover:text-orange-500 transition-colors cursor-pointer">Bathrooms</a>
                <a href="#amenities" className="block hover:text-orange-500 transition-colors cursor-pointer">Laundry</a>
              </div>
            </div>
            
            <div>
              <h6 className="text-gray-900 font-semibold uppercase tracking-wider text-sm mb-6">Support</h6>
              <div className="space-y-4 text-gray-600">
                <a href="#booking" className="block hover:text-orange-500 transition-colors cursor-pointer">How to Book</a>
                <a href="mailto:info@moshimoshi.info" className="block hover:text-orange-500 transition-colors cursor-pointer">Contact</a>
                <a href="https://moshimoshi.info" rel="nofollow" className="block hover:text-orange-500 transition-colors cursor-pointer">MOSHI MOSHI</a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-orange-200 mt-16 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-sm text-gray-500 mb-4 md:mb-0">
                © 2026 coliving HIDA TAKAYAMA is operated by NPO MOSHI MOSHI. All rights reserved.
              </div>
              
              <div className="flex items-center space-x-6 mb-4 md:mb-0">
                <a href="https://www.instagram.com/moshi_moshi_hida/" rel="nofollow" className="hover:text-orange-500 transition-colors cursor-pointer">
                  <i className="ri-instagram-line mr-1"></i>
                </a>
                <a href="https://www.facebook.com/" rel="nofollow" className="hover:text-orange-500 transition-colors cursor-pointer">
                  <i className="ri-facebook-line mr-1"></i>
                </a>
              </div>
              
              <div className="text-sm text-gray-500">
                <a href="/privacy-policy" className="hover:text-orange-500 transition-colors cursor-pointer">Privacy Policy</a>
                <span className="mx-2">•</span>
                <a href="/legal-notice" className="hover:text-orange-500 transition-colors cursor-pointer">Legal Notice</a>
                <span className="mx-2">•</span>
                <a href="/terms-of-service" className="hover:text-orange-500 transition-colors cursor-pointer">Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
